import React, { useState, useEffect, useContext, useMemo } from "react";
import styles from "./ForWhomMainPage.module.scss";
import { useFormikContext } from "formik";
import {
  OwcTypography,
  OwcListItem,
  OwcIconButton,
  OwcInput,
  OwcTextarea,
  OwcIcon,
  OwcRadioGroup,
  OwcRadio,
  OwcCheckbox,
} from "@one/react";
import {
  WHOM_TAB_LABEL,
  MAX_COUNT_CAPACITY,
  ATTRIBUTES_HEADING,
  CLUSTER_DETAILS_ATTRIBUTE_META,
  STANDALONE_ATTRIBUTE_META,
  entryType,
  DURATION,
} from "../../../constants";
import { withApollo, compose } from "react-apollo";
import { connect, useSelector } from "react-redux";
import { loadUserProjects as loadUserProjectsAction } from "../../user/redux/actions";
import { GET_AVAILABLE_PROJECTS } from "../../../gql/bookingapi/queries";
import { sortBy } from "lodash";
import PopOverModal from "../../../components/shared/CommonPopOverModal/PopOverModal";
import { GridAutoCompleteStyles } from "../../../components/shared/GridAutoCompleteStyles";
import { formatUserDisplay, uniqList } from "../../../utils/helpers/text/index";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { loadBookingInstruments as loadBookingInstrumentsAction } from "../../booking-instruments/redux/actions";
import { loadPendingReservations as loadPendingReservationsAction } from "../../booking-calendars/redux/actions";
import { CustomFilterContext } from "../../booking-instruments/booking-instruments-custom-filter/context";
import useDialog from "../../../utils/hooks/useDialog";
import PopoverAttributesDetails from "../../../components/shared/CommonPopOverModal/PopoverAttributesDetails";
import PopOverClusterAgGridTable from "../../../components/shared/CommonPopOverModal/PopOverClusterAgGridTable";
import { BookingContext, BookingPopUpContext } from "../../booking/context";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { ActionTypeSelect, ElnExperimentField } from "@digitallab/grid-common-components";
import AssistenceDeclaration from "../../../components/AssistenceDeclaration";
import HazardousDeclaration from "../../../components/HazardousDeclaration";

const ForWhomMainPage = ({
  loadBookingInstruments,
  loadPendingReservations,
  loadUserProjects,
  client
}) => {
  const actionTypes = Object.values(useSelector((store) => store.actionTypes));
  const formik = useFormikContext();
  const { isEditMode, editBookingData, isDuplicate } = useContext(BookingContext);
  const [bookingKind, setBookingKind] = useState(
    formik.values.bookingType === "Run" ? "Run" : "Action",
  );

  const { dispatchAction: bookingPopUpDispatchAction } =
    useContext(BookingPopUpContext);
  const { setSelectedList } = useContext(CustomFilterContext);
  const usersList = useSelector((state) => state.users.users);
  const storeUser = useSelector((store) => store.user);
  const isPredUser = storeUser.isPredUser;
  const { openDialog, ...dialogProps } = useDialog();
  const bookingInstruments = useSelector((store) => store.bookingInstruments);
  const { pendingReservations } = useSelector((state) => state.reservations);
  const [instrumentDetails, setInstrumentDetails] = useState(
    bookingInstruments.bookingInstruments,
  );
  const [manualProjectInput, setManualProjectInput] = useState(false);
  const [isProjectSetFromRedpandaProjects, setIsProjectSetFromRedpandaProjects] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [popoverObject, setpopoverObject] = useState({
    equipmentData: {},
    eventData: {},
  });

  const [showHazardousDeclaration, setShowHazardousDeclaration] = useState(false);
  const [showAssistedDecleration, setShowAssistedDecleration] = useState(false);

  const [isRefreshingProjects, setIsRefreshingProjects] = useState(false);

  const equipmentData = useMemo(() => popoverObject?.equipmentData,
    [popoverObject?.equipmentData])

  const showRedpandaErrorToast = () => snackbarService.show({
    message: `Unable to retrieve available projects, ${!!localStorage.getItem("secondaryAuthAccessToken") ? 'try refresh on the project field or ' : ''}enter project manually.`,
    type: "error",
    duration: DURATION,
  });

  useEffect(() => {
    const { withAssistance, hasHazardousMaterials } = formik.values;
    if (withAssistance !== undefined) {
      setShowAssistedDecleration(true);
    } else setShowAssistedDecleration(false);

    if (hasHazardousMaterials !== undefined) {
      setShowHazardousDeclaration(true);
    } else setShowHazardousDeclaration(false);

  }, [formik.values]);

  const refreshRedPandaProjects = async () => {
    if (!isPredUser || isRefreshingProjects) return;

    setIsRefreshingProjects(true);
    try {
      const jwtToken = localStorage.getItem("secondaryAuthAccessToken");
      const userId = storeUser?.id || storeUser?.userId || storeUser?.user;
      const email = storeUser?.email;
      if (!jwtToken) return
      const result = await client.query({
        query: GET_AVAILABLE_PROJECTS,
        fetchPolicy: "network-only",
        variables: {
          userId,
          email,
          token: jwtToken,
          timeoutMs: 24000,
        },
      });
      const projects = result?.data?.getAvailableProjectsFromRedPanda;
      if (Array.isArray(projects)) {
        loadUserProjects(projects);
      } else {
        showRedpandaErrorToast()
      }
    } catch (e) {
      showRedpandaErrorToast()
    } finally {
      setIsRefreshingProjects(false);
    }
  };

  useEffect(() => {
    if (isPredUser && (storeUser?.redPandaProjects === null || storeUser?.redPandaProjects?.length === 0)) {
      showRedpandaErrorToast()
    }
  }, [storeUser?.redPandaProjects, isPredUser]);

  useEffect(() => {
    if (Array.isArray(storeUser?.redPandaProjects)) {
      const mapped = storeUser.redPandaProjects.map(project => {
        const name = (project.projectShortName && project.themeNumber) ? `${project.themeNumber} - ${project.projectShortName}` : (project.projectId || "-")
        return {
          value: {
            themeNumber: project.themeNumber,
            projectId: project.projectId,
            projectShortName: project.projectShortName,
            projectName: project.projectName,
          },
          name
        }
      });
      setProjectOptions(mapped);
    } else {
      setProjectOptions([]);
    }
  }, [storeUser?.redPandaProjects]);

  useEffect(() => {
    if (isEditMode || isDuplicate) {
      const currentProjectId = formik.values.project;
      if (currentProjectId && projectOptions.length > 0 && !isProjectSetFromRedpandaProjects) {
        const matched = projectOptions.find(p => p.value?.projectId === currentProjectId);
        if (matched) {
          setManualProjectInput(false);
          formik.setFieldValue("project", "");
          formik.setFieldValue("redPandaProjects", [matched.value]);
          setIsProjectSetFromRedpandaProjects(true);
        }
      }

      const fromBooking = Array.isArray(formik.values.redPandaProjects)
        ? formik.values.redPandaProjects
        : [];

      if (fromBooking.length && !isProjectSetFromRedpandaProjects) {
        setManualProjectInput(false);
        setProjectOptions((prev) => {
          const existingIds = new Set(
            prev.map((o) => o.value?.projectId).filter(Boolean)
          );
          const additions = fromBooking
            .filter((p) => p?.projectId && !existingIds.has(p.projectId))
            .map((p) => ({
              value: p,
              name: p.themeNumber,
            }));
          return additions.length ? [...prev, ...additions] : prev;
        });


        formik.setFieldValue("redPandaProjects", fromBooking);
        setIsProjectSetFromRedpandaProjects(true);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, projectOptions, formik.values.project, formik.values.redPandaProjects, manualProjectInput]);

  useEffect(() => {
    if ((isEditMode && !formik.values?.redPandaProjects?.length) || !isPredUser) {
      setManualProjectInput(true)
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isPredUser, formik.values.redPandaProjects]);

  useEffect(() => {
    let pendingReservationIDs = [...pendingReservations];
    pendingReservationIDs = pendingReservationIDs.map((k) => k.resource);
    let result =
      (instrumentDetails.length !== 0 &&
        instrumentDetails.filter((a) =>
          pendingReservationIDs.includes(a.inventoryId),
        )) ||
      [];
    setInstrumentDetails(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReservations]);

  let userDetail = useMemo(() => [
    {
      key: storeUser.email,
      familyName: storeUser.familyName,
      givenName: storeUser.givenName,
      name: storeUser.name,
      userId: storeUser.user,
      value: formatUserDisplay(storeUser),
      email: storeUser.email,
    },
  ], [storeUser]);

  const bookingCreatedBy = editBookingData?.createdBy ?? "";
  let list = usersList.map((item) => ({
    ...item,
    key: item.email,
    value: formatUserDisplay(item),
  }));

  if (isEditMode) {
    userDetail = list?.filter((k) => k.userId === bookingCreatedBy);
    // Ensure userDetail has at least one element to prevent undefined errors
    if (userDetail.length === 0) {
      userDetail = [
        {
          key: storeUser.email,
          familyName: storeUser.familyName,
          givenName: storeUser.givenName,
          name: storeUser.name,
          userId: storeUser.user,
          value: formatUserDisplay(storeUser),
          email: storeUser.email,
        },
      ];
    }
  }
  const fixedOptions = useMemo(() => [userDetail[0]], [userDetail]);
  const [value, setValue] = useState([
    ...fixedOptions,
    ...formik?.values?.bookingFor?.filter(
      (k) => k && k.userId !== userDetail[0]?.userId
    ),
  ]);

  list = list.filter((a) =>
    isEditMode ? a.userId !== bookingCreatedBy : a.key !== storeUser.email,
  );
  list = sortBy(list, "value");

  let listOption = [];
  listOption = uniqList([...userDetail, ...list], "value");

  //TODO: validation on formik side to not allow bookingFor to be empty
  useEffect(() => {
    if (formik.values.bookingFor.length === 0) {
      setValue([...fixedOptions]);
    }
  }, [fixedOptions, formik.values.bookingFor.length])

  useEffect(() => {
    if (isEditMode) {
      if (editBookingData) {
        editBookingData.bookedUsers = (editBookingData.bookedUsers || []).filter(Boolean);
      }
      let bookedUser = editBookingData?.bookedUsers.map((item) => ({
        ...item,
        key: item.email,
        userId: item.user || item.userId,
        value: formatUserDisplay(item),
      }));
      let existingUsersID = (bookedUser || []).map((k) => k.userId);
      let updatedUsers = (formik?.values?.bookingFor || []).filter(
        (k) => !existingUsersID?.includes(k.userId),
      );
      let updatedBookedUsers = (formik?.values?.bookingFor || []).map((i) =>
        bookedUser?.find((k) => i.userId === k.userId),
      );
      updatedBookedUsers = updatedBookedUsers?.filter((v) => v !== undefined);
      updatedBookedUsers = updatedBookedUsers?.filter(
        (k) => k.userId !== userDetail[0]?.userId,
      );
      if (updatedUsers.length === 0) {
        setValue([
          ...fixedOptions,
          ...updatedBookedUsers.filter((k) => k.userId !== bookingCreatedBy),
        ]);
      } else if (updatedUsers.length > 0) {
        const finalUsersList = [...fixedOptions, ...updatedBookedUsers, ...updatedUsers].map(user => {
          return {
            ...user,
            userId: user.userId || bookedUser.find(e => e.email === user.email)?.userId
          }
        }).filter((u, i, arr) => arr.findIndex(x => x.userId === u.userId) === i);
        setValue(finalUsersList);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      bookingFor: value,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOnClose = () => {
    bookingPopUpDispatchAction({
      type: "reset",
    });
  };

  const onClear = (ev) => {
    let result = instrumentDetails.filter((a) => a !== ev?.target?.value);
    let remainingPendingReservation = [...pendingReservations].filter(
      (a) => a.resource !== ev?.target?.value.inventoryId,
    );
    if (!result.length) {
      setInstrumentDetails([]);
      updateEquipmentDetailsInRedux([]);
      loadPendingReservations([]);
      setSelectedList([]);
    } else {
      setInstrumentDetails(result);
      updateEquipmentDetailsInRedux(result);
      loadPendingReservations([...remainingPendingReservation]);
      setSelectedList(() => {
        return result.map((k) => k.inventoryId);
      });
    }
  };

  useEffect(() => {
    if (instrumentDetails.length === 0) {
      formik.setValues({
        ...formik.values,
        description: "",
        project: "",
        redPandaProjects: [],
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentDetails]);

  const renderProjectTags = (selectedOptions) => {
    const labels = selectedOptions.map((opt) => opt.name || opt.value?.project_id || "");
    if (!labels.length) return null;
    const first = labels[0];
    const rest = labels.slice(1);
    const maxCapacity = MAX_COUNT_CAPACITY - first.length;
    let remainingCapacity = maxCapacity - 7;
    if (remainingCapacity < 0) remainingCapacity = 0;
    return (
      <>
        <OwcTypography className={styles.renderTextField}>
          {first}
        </OwcTypography>

        {rest[0] && (
          <OwcTypography style={{ marginLeft: "5px" }}>
            {rest[0].length > remainingCapacity
              ? `${rest[0].substring(0, remainingCapacity)}...`
              : rest[0]}
          </OwcTypography>
        )}

        {rest.length > 1 && (
          <span style={{ color: "#0066CC", marginLeft: "5px" }}>
            +{rest.length - 1}
          </span>
        )}
      </>
    );
  };



  const renderTags = (selectedObjects) => {
    let renderTagValue = selectedObjects?.map((selectedObject) => {
      const chipLabel = selectedObject?.value ?? selectedObject;
      return selectedObjects.length === 1 ? `${chipLabel}` : `${chipLabel}, `;
    });
    let maxCapacity = MAX_COUNT_CAPACITY - renderTagValue[0].length;
    let remainingCapacity = maxCapacity - 7;
    if (remainingCapacity < 0) {
      remainingCapacity = 0;
    }
    return (
      <>
        <OwcTypography className={styles.renderTextField}>
          {renderTagValue[0]}
        </OwcTypography>
        {renderTagValue.length > 1 && (
          <OwcTypography style={{ marginLeft: "5px" }}>
            {renderTagValue[1].length > remainingCapacity
              ? `${renderTagValue[1].substring(0, remainingCapacity)}...`
              : renderTagValue[1]}
          </OwcTypography>
        )}
        {renderTagValue.length > 2 ? (
          <span style={{ color: "#0066CC", marginLeft: "5px" }}>
            +{renderTagValue.length - 2}
          </span>
        ) : (
          ""
        )}
      </>
    );
  };

  const updateEquipmentDetailsInRedux = async (result) => {
    let bookingInstruments = result;
    loadBookingInstruments({
      bookingInstruments: bookingInstruments,
    });
  };

  const inputChangeHandler = (event) => {
    setBookingKind(event.detail);
    formik.setFieldValue('bookingType', event.detail === "Run" ? "Run" : null);
  };

  return (
    <>
      <div className={styles.flexContainer}>
        <div className={styles.flexLeftColumn}>
          <div className={styles.lineTitle}>
            <OwcTypography
              variant="subtitle2"
              className={styles.typographyTitle}
            >
              Equipment details
            </OwcTypography>
          </div>
          <div className={styles.scrollBarlist}>
            {Array.isArray(instrumentDetails) && instrumentDetails.length !== 0 &&
              instrumentDetails.map((item, index) => {
                const option = item.shortIdentifier;
                return (
                  <React.Fragment key={index}>
                    <OwcListItem
                      value={option}
                      key={option}
                      onClick={(e) => {
                        openDialog();
                        setpopoverObject({
                          equipmentData: item,
                          eventData: {},
                        });
                      }}
                      noBorder
                      className="owcListItemBorder"
                    >
                      <div style={{ display: "flex" }}>
                        <div style={{ width: "93%", paddingTop: "7px" }}>
                          {option}
                        </div>
                        <div style={{ width: "8%" }}>
                          <OwcIconButton
                            value={item}
                            key={option}
                            data-testid="clear-selected-button"
                            type="filled"
                            icon="circle_clear"
                            flat
                            onClick={(ev) => {
                              ev.stopPropagation();
                              const isAnyOnGoingEvent =
                                pendingReservations.filter(
                                  (x) =>
                                    x.resource ===
                                    ev.target.value.inventoryId &&
                                    x.isOngoingEventFlag,
                                );
                              if (isEditMode && isAnyOnGoingEvent.length > 0) {
                                bookingPopUpDispatchAction({
                                  type: "onGoingBooking",
                                  payload: {
                                    onApprove: handleOnClose,
                                  },
                                });
                              } else {
                                bookingPopUpDispatchAction({
                                  type: "futureBooking",
                                  payload: {
                                    onApprove: () => onClear(ev),
                                    onclose: handleOnClose,
                                  },
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    </OwcListItem>
                  </React.Fragment>
                );
              })}
          </div>
        </div>
        <div className={styles.flexRightColumn}>
          <div className={styles.lineTitle}>
            <OwcTypography
              variant="subtitle2"
              className={styles.typographyTitle}
            >
              Booking details
            </OwcTypography>
          </div>
          <div>
            <div
              style={{
                padding: "10px 20px 10px 20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: bookingKind === "Run" ? "start" : "flex-end",
                }}
              >
                <OwcRadioGroup
                  value={bookingKind}
                  onInputChange={inputChangeHandler}
                >
                  <OwcRadio value="Run">Run</OwcRadio>
                  <OwcRadio value="Action">Action</OwcRadio>
                </OwcRadioGroup>

                {bookingKind === "Action" && (
                  <ActionTypeSelect
                    actionTypes={actionTypes}
                    formik={formik}
                    name="bookingType"
                  />
                )}


              </div>
            </div>
            <Autocomplete
              multiple
              data-testid="whom-tab-field-booking-for"
              value={value}
              limitTags={2}
              className={styles.bookForFilled}
              disableCloseOnSelect
              onChange={(_event, newValue) => {
                newValue = Array.isArray(newValue) ? newValue : [newValue]
                setValue([
                  ...fixedOptions,
                  ...newValue.filter((a) => (isEditMode ? (a && a.userId !== bookingCreatedBy) : (a && a.key !== storeUser.email))),
                ])
              }}
              name="bookingFor"
              options={listOption}
              fullWidth={false}
              getOptionLabel={(option) => {
                if (Array.isArray(option) && option.length > 0) {
                  return option[0]?.value;
                }
                return option?.value || "";
              }}
              renderTags={renderTags}
              isOptionEqualToValue={(option, value) => {
                return (value?.userId && option?.userId) && value.userId === option.userId
              }
              }
              getOptionDisabled={(option) => {
                return (option && userDetail[0]) && option.value === userDetail[0].value;
              }}
              selectOnFocus
              clearOnBlur
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={WHOM_TAB_LABEL.BOOKING_FOR}
                  variant="filled"
                  required={true}
                  error={
                    formik.touched["bookingFor"] === true &&
                      formik.values["bookingFor"].length === 0
                      ? true
                      : false
                  }
                  helpertext={
                    formik.touched["bookingFor"] === true &&
                      formik.values["bookingFor"].length === 0 &&
                      formik.errors["bookingFor"]
                      ? formik.errors["bookingFor"]
                      : ""
                  }
                />
              )}
              sx={GridAutoCompleteStyles?.gridAutoCompleteLableShrink}
            />

            <div className={styles.projectContainer} >
              {isPredUser && (<OwcCheckbox
                checked={manualProjectInput}
                onInputChange={() => {
                  const next = !manualProjectInput;
                  setManualProjectInput(next);

                  if (next) {
                    formik.setFieldValue("redPandaProjects", []);
                  } else {
                    formik.setFieldValue("project", "");
                  }
                }}
                label="Enter project manually"
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <OwcTypography style={{ fontFamily: "inherit", fontSize: "16px" }}>
                    Enter project manually
                  </OwcTypography>
                </div>
              </OwcCheckbox>)}
              {!manualProjectInput ? (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  style={{ flex: 1 }}
                  limitTags={3}
                  popupIcon={null}
                  forcePopupIcon={false}
                  data-testid="whom-tab-field-project"
                  options={projectOptions}
                  required={isPredUser}
                  value={projectOptions.filter((opt) =>
                    (formik.values.redPandaProjects || []).some(
                      (p) =>
                        (p.projectId && opt.value.projectId && p.projectId === opt.value.projectId)
                    )
                  )}
                  getOptionLabel={(option) => option.name || option.value?.themeNumber || "-"}
                  isOptionEqualToValue={(opt, val) =>
                    (opt.value?.projectId && val.value?.projectId && opt.value.projectId === val.value.projectId)
                  }
                  renderTags={renderProjectTags}
                  onChange={(_event, newValue) => {
                    const limited =
                      newValue.length > 10 ? newValue.slice(0, 10) : newValue;

                    if (newValue.length > 10) {
                      snackbarService.show({
                        message: "You can select up to 10 projects.",
                        type: "warning",
                        duration: DURATION,
                      });
                    }

                    formik.setFieldValue(
                      "redPandaProjects",
                      limited.map((p) => p.value)
                    );
                  }}
                  renderInput={(params) => {
                    const hasSelectedProjects = (formik.values.redPandaProjects || []).length > 0;
                    const hideRefresh = hasSelectedProjects && projectOptions.length;

                    return (
                      <div style={{ position: "relative", width: "100%" }}>
                        <TextField
                          {...params}
                          label={WHOM_TAB_LABEL.PROJECTS}
                          variant="filled"
                          required={isPredUser}
                          error={
                            formik.touched["redPandaProjects"] &&
                            Boolean(formik.errors["redPandaProjects"])
                          }
                          helperText={
                            formik.touched["redPandaProjects"] && formik.errors["redPandaProjects"]
                              ? formik.errors["redPandaProjects"]
                              : ""
                          }
                          InputProps={{
                            ...params.InputProps,
                            style: {
                              ...(params.InputProps?.style || {}),
                              paddingRight: "44px",
                            },
                          }}
                        />

                        {(isPredUser && !!localStorage.getItem("secondaryAuthAccessToken") && !hideRefresh) && (
                          <OwcIconButton
                            type="filled"
                            icon="refresh"
                            flat
                            disabled={isRefreshingProjects}
                            className={isRefreshingProjects ? styles.spin : undefined}
                            onClick={(ev) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                              refreshRedPandaProjects();
                            }}
                            style={{
                              position: "absolute",
                              right: "6px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 2,
                            }}
                            aria-label="Refresh RedPanda projects"
                          />
                        )}
                      </div>
                    )
                  }}
                />
              ) : (
                <OwcInput
                  data-testid="whom-tab-field-project"
                  label={WHOM_TAB_LABEL.PROJECTS + (isPredUser ? ' *' : '')}
                  style={{ width: "100%" }}
                  name="project"
                  value={formik.values.project}
                  onBlur={formik.handleBlur}
                  maxLength={30}
                  minLength={0}
                  required={isPredUser}
                  onInputChange={(ev) => {
                    formik.setFieldValue("project", ev.detail);
                  }}
                >
                  {formik.values.project !== "" && (
                    <OwcIcon
                      slot="suffix"
                      type="filled"
                      name="circle_clear"
                      onClick={() => formik.setFieldValue("project", "", true)}
                      id="whom-tab-field-project-clear"
                    />
                  )}
                </OwcInput>
              )}
            </div>
            <div className={styles.textFilled} style={{ paddingBottom: 0 }}>
              <OwcTextarea
                name="description"
                data-testid="whom-tab-field-description"
                label={WHOM_TAB_LABEL.DESCRIPTION + (!isPredUser ? ' *' : '')}
                value={formik.values.description}
                onBlur={formik.handleBlur}
                onInputKeyDown={(ev) => {
                  formik.setFieldValue("description", ev.detail);
                }}
                onInputChange={(ev) => {
                  formik.setFieldValue("description", ev.detail);
                }}
                onInputReset={() =>
                  formik.setFieldValue("description", "", true)
                }
                maxLength={200}
                minLength={0}
                cols="18"
                rows={4}
                required
                reset
                errorText={formik.errors["description"]}
                validation-mode="realtime"
              />
            </div>
            {bookingKind === "Run" && (
              <div className={styles.textFilled} style={{ paddingBottom: "20px", display: 'flex' }}>
                <ElnExperimentField value={formik.values.elnId} onValueChange={(elnId) => formik.setFieldValue('elnId', elnId)} client={client} />
              </div>
            )}

            <div className={styles.textFilled}>
              {/* Lab Safety Declaration Section - Only show if equipment requires hazardous approval */}
              {bookingKind === "Run" && showHazardousDeclaration && (
                <HazardousDeclaration />
              )}

              {bookingKind === "Run" && showAssistedDecleration && (
                <AssistenceDeclaration />
              )}
            </div>
          </div>
        </div>
      </div>
      <PopOverModal
        title={popoverObject?.equipmentData?.shortIdentifier}
        data={popoverObject}
        closeTitle="Close"
        onDialogCloseCallBack={() =>
          setpopoverObject({
            equipmentData: {},
            eventData: {},
          })
        }
        {...dialogProps}
      >
        <PopoverAttributesDetails
          title={ATTRIBUTES_HEADING}
          metaData={
            popoverObject?.equipmentData?.entryType === entryType.cluster
              ? CLUSTER_DETAILS_ATTRIBUTE_META
              : STANDALONE_ATTRIBUTE_META
          }
          data={equipmentData}
        />
        {popoverObject?.equipmentData?.entryType === entryType.cluster && (
          <PopOverClusterAgGridTable data={[popoverObject?.equipmentData]} />
        )}
      </PopOverModal>
    </>
  );
};

export default compose(
  connect(null, {
    loadBookingInstruments: loadBookingInstrumentsAction,
    loadPendingReservations: loadPendingReservationsAction,
    loadUserProjects: loadUserProjectsAction
  }),
  withApollo,
)(ForWhomMainPage);
